// #region IMPORT
import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Typography,
  List,
  ListItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Button, Checkbox, Combo, Dialog } from '../../Components/MUIs';
import { KakaoPost } from '../../Components/Kakaos';
import { ArrowForwardIos as RightIcon } from '@mui/icons-material';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import Commons from '../../Services/Common/Common';
import Global from '../../Services/Common/Global';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapacitorApp } from '@capacitor/app';
import { MOK_SIGNUP_RESULT_KEY } from './IdentityMokCallback';
import {
  StyledLabel,
  StyledTextField,
  comboWrapStyles,
} from '../../Components/Ui/FormStyles';
import SuccessAnimation from '../../Assets/animations/Success.json';
import Lottie from 'lottie-react';
import { useSelector, useDispatch } from 'react-redux';
import { CommonActions } from '../../Reducer/CommonReducer';
import Terms from '../Login/Terms';
import { useBackHandler } from '../../Hooks/back/useBackHandler';
// #endregion

const steps = ['약관', '회원 유형', '정보 입력', '가입 완료'];

function SignUp(props) {
  // #region 변수
  const dispatch = useDispatch();
  const { navBar } = useSelector((state) => state.systemBar);
  const activeStep = useSelector((state) => state.common.activeStep);
  const closeDialog = props.CloseDialog;
  const setSignUpInputs = props.setSignUpInputs;
  const [skipped, setSkipped] = React.useState(new Set());
  const [dialog1, setDialog1] = React.useState(false);
  const [dialog2, setDialog2] = React.useState({
    open: false,
    type: '',
    title: '',
  }); // 약관 여는 Toggle
  const lottieRef = useRef(null);
  const userIdInputRef = useRef(null);
  const [userIdError, setUserIdError] = React.useState(false);
  const [custCodeTipOpen, setCustCodeTipOpen] = React.useState(false);
  const [custCodeHowTipOpen, setCustCodeHowTipOpen] = React.useState(false);
  const [backClick, setBackClick] = React.useState(null);
  const [mokLoading, setMokLoading] = React.useState(false);
  const mokDeepLinkListenerRef = useRef(null);
  const isExternalSignUp = props.signUpInputs.memberSignUpType === 'EXTERNAL';
  // #endregion

  // #region 커스텀훅
  // 뒤로가기 핸들러 설정
  useBackHandler({
    backClick,
    setBackClick,
    redirectPath: null,
    setRedirectPath: () => { },
    sheetTypeRef: null,
  });
  // #endregion

  // #region useEffect
  // 생년월일 파싱
  const ParseBirth = useCallback((raw) => {
    if (!raw) return '';
    const s = String(raw).trim();

    // 이미 input[type="date"] 형식이면 그대로 사용
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // 숫자만 있는 케이스 (YYYYMMDD)
    const digits = s.replace(/[^0-9]/g, '');
    if (digits.length === 8) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }

    return '';
  }, []);

  // 본인인증 결과를 입력 필드(setSignUpInputs)에 적용
  const ApplyMokResultToInputs = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') return;

      const name = payload.userName ?? '';
      const tel = payload.userPhone ?? '';
      const birthRaw = payload.userBirthday ?? '';
      const birth = ParseBirth(birthRaw);

      // 서버(ResultController)에서 내려주는 본인인증 결과 원본을 통째로 보관 (유지보수/추적용)
      const mokResult = {
        resultCode: payload.resultCode,
        resultMsg: payload.resultMsg,
        userName: payload.userName,
        siteId: payload.siteId,
        txId: payload.txId,
        providerId: payload.providerId,
        serviceType: payload.serviceType,
        ci: payload.ci,
        di: payload.di,
        userPhone: payload.userPhone,
        userBirthday: payload.userBirthday ?? payload.userBirth,
        userGender: payload.userGender,
        userNation: payload.userNation,
        reqAuthType: payload.reqAuthType,
        reqDate: payload.reqDate,
        issuer: payload.issuer,
        issueDate: payload.issueDate,
      };

      setSignUpInputs((prev) => ({
        ...prev,
        identityConfirm: true,
        identityErrMsg: '',
        ...mokResult,
        userNm: name,
        realName: name,
        userTel: tel,
        userId: tel,
        userBirth: birth,
      }));
    },
    [ParseBirth, setSignUpInputs]
  );

  // PWA redirect 콜백 결과(sessionStorage)를 SignUp에서 단일 처리
  useEffect(() => {
    const stored = sessionStorage.getItem(MOK_SIGNUP_RESULT_KEY);
    if (!stored) return;
    try {
      const result = JSON.parse(stored);
      ApplyMokResultToInputs(result);
    } catch (_) {
      // 무효한 값이면 그냥 제거
    } finally {
      sessionStorage.removeItem(MOK_SIGNUP_RESULT_KEY);
    }
  }, [ApplyMokResultToInputs]);

  // 네이티브 딥링크 리스너 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (mokDeepLinkListenerRef.current) {
        mokDeepLinkListenerRef.current.remove();
        mokDeepLinkListenerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (activeStep !== 3 || !lottieRef.current) return;

    lottieRef.current.setSpeed(1);

    const timeoutId = setTimeout(() => {
      lottieRef.current?.pause();
    }, 1800);

    return () => clearTimeout(timeoutId);
  }, [activeStep]);

  // 뒤로가기 동작 설정
  useEffect(() => {
    // dialog1이나 dialog2가 열려있으면 먼저 닫기
    if (dialog1 || dialog2.open) {
      setBackClick({
        fn: () => {
          if (dialog2.open) {
            setDialog2((prev) => ({ ...prev, open: false }));
          } else if (dialog1) {
            setDialog1(false);
          }
        },
        clearAfter: true,
      });
      return;
    }

    // activeStep이 0이면 LoginFunction의 Dialog 닫기
    if (activeStep === 0) {
      setBackClick({
        fn: () => {
          closeDialog();
        },
        clearAfter: true,
      });
      return;
    }

    // 그 외에는 activeStep 감소
    // if (activeStep > 0) {
    //   setBackClick(null);
    //   return;
    // }
  }, [activeStep, dialog1, dialog2.open, closeDialog]);
  // #endregion

  // #region 함수
  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const HandleNext = async (_current) => {
    let newSkipped = skipped;
    let signAddress = `User_Info/${props.signUpInputs.userId}/Sign`;

    let healthAddress = `User_Info/${props.signUpInputs.userId}/Health`;
    if (_current === 2) {
      if (!props.SignUpIndex2Validation()) {
        return false;
      } else {
        // 마지막 단계에서 저장 로직 실행
        const result = await props.SaveLogic2(signAddress, healthAddress);
        // 저장 실패 시 다음 스텝으로 이동하지 않음
        if (result === false) {
          // 아이디·등록코드 필드로 스크롤 및 에러 표시
          setUserIdError(true);
          setTimeout(() => {
            if (userIdInputRef.current) {
              userIdInputRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 100);
          return false;
        }
      }
    }

    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    dispatch(CommonActions.increaseStep());
    setSkipped(newSkipped);
  };

  const HandleBack = () => {
    dispatch(CommonActions.decreaseStep());
  };

  const HandleSelectMemberType = (type) => {
    setSignUpInputs((prev) => ({
      ...prev,
      memberSignUpType: type,
      userType: type === 'INTERNAL' ? 'EMPLOYEE' : 'PARTNER',
      idDuplicationCheck: false,
      idDuplicationMsg: '',
      ...(type === 'EXTERNAL'
        ? { userDepCd: '', userBlood: '' }
        : { affiliationNm: '' }),
    }));
  };

  // 본인인증 클릭 이벤트
  const HandleMokAuthClick = async () => {
    setMokLoading(true);
    setSignUpInputs((prev) => ({
      ...prev,
      identityErrMsg: '',
    }));

    try {
      // 네이티브 환경 — 시스템 브라우저로 인증, 딥링크로 복귀
      if (Capacitor.isNativePlatform()) {
        const nativeReturnUrl = `${process.env.REACT_APP_MOK_RETURN_URL}/mok-callback?type=signup&from=native`;

        // 이전 리스너 정리
        if (mokDeepLinkListenerRef.current) {
          mokDeepLinkListenerRef.current.remove();
          mokDeepLinkListenerRef.current = null;
        }

        const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}?returnUrl=${encodeURIComponent(nativeReturnUrl)}`;
        const httpRes = await CapacitorHttp.get({ url: mokRequestUrl });
        const mokResData = httpRes.data;

        let nativeMokUrl = null;

        if (
          mokResData &&
          typeof mokResData === 'object' &&
          mokResData.encryptReqClientInfo
        ) {
          const sdkOrigin = new URL(process.env.REACT_APP_MOBILE_OK).origin;
          const mokBaseUrl = `${sdkOrigin}/ptb_mokauth.html`;

          // PWA SDK가 구성하는 URL과 파라미터 순서/항목을 동일하게 맞춤
          // (convertInfo, encryptVersion, callbackFunction, alternative 누락 시 5028 발생 가능)
          const frontendHostname = new URL(process.env.REACT_APP_MOK_RETURN_URL)
            .hostname;
          const params = new URLSearchParams({
            usageCode: mokResData.usageCode || '01001',
            serviceId: mokResData.serviceId,
            convertInfo: 'undefined',
            returnUrl: mokResData.returnUrl,
            encryptVersion: 'undefined',
            encryptReqClientInfo: mokResData.encryptReqClientInfo,
            serviceType: mokResData.serviceType || 'telcoAuth',
            retTransferType: mokResData.retTransferType || 'MOKToken',
            callbackFunction: '',
            alternative: frontendHostname,
          });
          nativeMokUrl = `${mokBaseUrl}?${params.toString()}`;
        }

        // HTML form 파싱 폴백 (응답이 문자열인 경우)
        if (!nativeMokUrl && typeof mokResData === 'string') {
          const doc = new DOMParser().parseFromString(mokResData, 'text/html');
          const form = doc.querySelector('form');
          if (form) {
            const formAction = form.getAttribute('action');
            const formParams = {};
            form.querySelectorAll('input').forEach((el) => {
              if (el.name) formParams[el.name] = el.value;
            });
            if (formAction && formParams.encryptReqClientInfo) {
              nativeMokUrl = `${formAction}?${new URLSearchParams(formParams).toString()}`;
            }
          }
        }

        if (!nativeMokUrl) {
          const preview = JSON.stringify(mokResData).substring(0, 300);
          throw new Error(
            `본인확인 URL 추출 실패 (status:${httpRes.status})\n${preview}`
          );
        }

        // 딥링크 수신 리스너 등록
        const handle = await CapacitorApp.addListener(
          'appUrlOpen',
          async (event) => {
            try {
              await Browser.close();
              if (!event.url.includes('mok-callback')) return;

              const url = new URL(event.url);
              const dataParam = url.searchParams.get('data');

              if (dataParam) {
                const parsed = JSON.parse(dataParam);
                if (
                  parsed?.resultCode &&
                  String(parsed.resultCode) !== '2000'
                ) {
                  throw new Error(
                    parsed?.resultMsg || '본인확인에 실패했습니다.'
                  );
                }
                ApplyMokResultToInputs(parsed);
              } else {
                throw new Error('본인확인 결과 정보가 없습니다.');
              }
            } catch (e) {
              props.setSignUpInputs((prev) => ({
                ...prev,
                identityErrMsg:
                  e?.response?.data?.message ||
                  e?.message ||
                  '본인인증 처리에 실패했습니다. 다시 시도해 주세요.',
              }));
            } finally {
              if (mokDeepLinkListenerRef.current) {
                mokDeepLinkListenerRef.current.remove();
                mokDeepLinkListenerRef.current = null;
              }
              setMokLoading(false);
            }
          }
        );

        mokDeepLinkListenerRef.current = handle;
        await Browser.open({ url: nativeMokUrl });
        // setMokLoading(false)는 딥링크 콜백에서 처리
        return;
      } else {
        // PWA 환경 — 기존 표준창 방식 유지
        const returnUrl = `${window.location.origin}/mok-callback?type=signup`;
        const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;

        // 공식 문서: redirect 방식 — callback "" 이면 표준창으로 URL 이동
        if (window.MOBILEOK && typeof window.MOBILEOK.process === 'function') {
          window.MOBILEOK.process(mokRequestUrl, 'MB', '');
          setMokLoading(false);
          return;
        }

        throw new Error('본인확인 SDK가 로드되지 않았습니다.');
      }
    } catch (e) {
      setMokLoading(false);
      const errMsg =
        e?.response?.data?.message ||
        e?.message ||
        '본인인증 요청에 실패했습니다. 다시 시도해 주세요.';
      setSignUpInputs((prev) => ({
        ...prev,
        identityErrMsg: errMsg,
      }));
    }
  };

  const RecievePosts = (_data) => {
    if (!_data.road_address) {
      return false;
    } else {
      props.setSignUpInputs((prev) => ({
        ...prev,
        userPost: _data.road_address.zone_no,
        userAddress1: _data.road_address.address_name,
      }));
    }
  };
  // #endregion

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'calc(100vh - 56px)',
        boxSizing: 'border-box',
        position: 'relative',
        overflowY: 'auto',
        px: 3,
        pb:
          activeStep === 3
            ? '0px'
            : activeStep === 2
              ? 'calc(120px + env(safe-area-inset-bottom, 0px))'
              : '88px',
      }}
    >
      <Helmet>
        {/* 개발 */}
        {/* <script src="https://scert.mobile-ok.com/resources/js/index.js"></script> */}
        {/* 운영 */}
        {/* <script src="https://cert.mobile-ok.com/resources/js/index.js"></script> */}

        <script src={process.env.REACT_APP_MOBILE_OK}></script>
      </Helmet>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mt: 2,
          '& .MuiStep-root': {
            padding: 0.5,
          },
          '& .MuiStepConnector-root': {
            padding: 0.5,
          },
          '& .MuiStepIcon-root.Mui-active': {
            color: '#2EBD36',
          },
          '& .MuiStepIcon-root.Mui-completed': {
            color: '#2EBD36',
          },
          '& .MuiStepLabel-label.Mui-active': {
            fontWeight: 600,
            color: '#111827',
          },
          '& .MuiStepLabel-label.Mui-completed': {
            color: '#374151',
          },
          '& .MuiStepConnector-line': {
            borderTopWidth: 2,
          },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
            borderColor: '#2EBD36',
          },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: '#2EBD36',
          },
          '& .MuiStepLabel-label': {
            fontSize: '11px',
            mt: 0.5,
          },
        }}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <React.Fragment>
        {activeStep === 0 ? (
          <Stack sx={{ mt: 3 }} spacing={2}>
            {/* 안내 문구 */}
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#111827',
                mb: 2,
              }}
            >
              서비스 이용을 위해
              <br />
              약관에 동의해 주세요.
            </Typography>

            {/* 약관 전체 동의 */}
            <Box
              sx={{
                p: 2,
                backgroundColor: '#F9FAFB',
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <Checkbox
                name={
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    약관 전체 동의
                  </Typography>
                }
                checked={
                  props.agreement.serviceUsingCheck &&
                  props.agreement.serviceKindCheck &&
                  props.agreement.personalInformationCheck &&
                  props.agreement.transferAndCompensationCheck &&
                  props.agreement.disclaimerCheck
                }
                onChange={(checked) => {
                  props.setAgreement({
                    ...props.agreement,
                    serviceUsingCheck: checked,
                    serviceKindCheck: checked,
                    personalInformationCheck: checked,
                    transferAndCompensationCheck: checked,
                    disclaimerCheck: checked,
                  });
                }}
              />
            </Box>

            {/* 구분선 */}
            <Box
              sx={{
                width: '100%',
                height: '1px',
                backgroundColor: '#E5E7EB',
                mt: '24px !important',
              }}
            />

            {/* 개별 약관 목록 */}
            <List sx={{ width: '100%', pt: 0 }}>
              {/* 필수 약관 1 */}
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setDialog2({
                        ...dialog2,
                        open: true,
                        type: '0',
                        title: '서비스 이용 목적 및 용어 동의',
                      })
                    }
                  >
                    <RightIcon sx={{ color: '#9CA3AF' }} />
                  </IconButton>
                }
              >
                <Checkbox
                  name={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#2EBD36',
                          }}
                        >
                          [필수]
                        </span>{' '}
                        서비스 이용 목적 및 용어 동의
                      </Typography>
                    </Box>
                  }
                  checked={props.agreement.serviceUsingCheck}
                  onChange={(checked) => {
                    props.setAgreement({
                      ...props.agreement,
                      serviceUsingCheck: checked,
                    });
                  }}
                />
              </ListItem>

              {/* 필수 약관 2 */}
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setDialog2({
                        ...dialog2,
                        open: true,
                        type: '1',
                        title: '서비스 종류 및 의무 동의',
                      })
                    }
                  >
                    <RightIcon sx={{ color: '#9CA3AF' }} />
                  </IconButton>
                }
              >
                <Checkbox
                  name={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#2EBD36',
                          }}
                        >
                          [필수]
                        </span>{' '}
                        서비스 종류 및 의무 동의
                      </Typography>
                    </Box>
                  }
                  checked={props.agreement.serviceKindCheck}
                  onChange={(checked) => {
                    props.setAgreement({
                      ...props.agreement,
                      serviceKindCheck: checked,
                    });
                  }}
                />
              </ListItem>

              {/* 필수 약관 3 */}
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setDialog2({
                        ...dialog2,
                        open: true,
                        type: '2',
                        title: '개인정보 수집 및 이용동의',
                      })
                    }
                  >
                    <RightIcon sx={{ color: '#9CA3AF' }} />
                  </IconButton>
                }
              >
                <Checkbox
                  name={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#2EBD36',
                          }}
                        >
                          [필수]
                        </span>{' '}
                        개인정보 수집 및 이용 동의
                      </Typography>
                    </Box>
                  }
                  checked={props.agreement.personalInformationCheck}
                  onChange={(checked) => {
                    props.setAgreement({
                      ...props.agreement,
                      personalInformationCheck: checked,
                    });
                  }}
                />
              </ListItem>

              {/* 필수 약관 4 */}
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setDialog2({
                        ...dialog2,
                        open: true,
                        type: '3',
                        title: '양도금지 및 손해배상 책임 동의',
                      })
                    }
                  >
                    <RightIcon sx={{ color: '#9CA3AF' }} />
                  </IconButton>
                }
              >
                <Checkbox
                  name={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#2EBD36',
                          }}
                        >
                          [필수]
                        </span>{' '}
                        양도금지 및 손해배상 책임 동의
                      </Typography>
                    </Box>
                  }
                  checked={props.agreement.transferAndCompensationCheck}
                  onChange={(checked) => {
                    props.setAgreement({
                      ...props.agreement,
                      transferAndCompensationCheck: checked,
                    });
                  }}
                />
              </ListItem>

              {/* 필수 약관 5 */}
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setDialog2({
                        ...dialog2,
                        open: true,
                        type: '4',
                        title: '면책 사항 동의',
                      })
                    }
                  >
                    <RightIcon sx={{ color: '#9CA3AF' }} />
                  </IconButton>
                }
              >
                <Checkbox
                  name={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#2EBD36',
                          }}
                        >
                          [필수]
                        </span>{' '}
                        면책 사항 동의
                      </Typography>
                    </Box>
                  }
                  checked={props.agreement.disclaimerCheck}
                  onChange={(checked) => {
                    props.setAgreement({
                      ...props.agreement,
                      disclaimerCheck: checked,
                    });
                  }}
                />
              </ListItem>
            </List>
          </Stack>
        ) : activeStep === 1 ? (
          <Stack sx={{ mt: 3, mb: 1 }} spacing={2.5}>
            <Box>
              <Typography
                sx={{
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#111827',
                  lineHeight: 1.45,
                }}
              >
                회원 유형 선택
              </Typography>
              <Typography
                sx={{
                  fontSize: '15px',
                  color: '#4B5563',
                  mt: 1.25,
                  lineHeight: 1.55,
                }}
              >
                본인에 해당하는 항목을 한 가지만 눌러 주세요.
              </Typography>
            </Box>

            <Stack spacing={2}>
              {[
                {
                  type: 'INTERNAL',
                  Icon: BadgeOutlined,
                  title: '우리 회사 직원(내부인)',
                },
                {
                  type: 'EXTERNAL',
                  Icon: HandshakeOutlined,
                  title: '협력업체·외부 담당자(외부인)',
                },
              ].map(({ type, Icon, title }) => {
                const selected = props.signUpInputs.memberSignUpType === type;
                return (
                  <Box
                    key={type}
                    onClick={() => HandleSelectMemberType(type)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        HandleSelectMemberType(type);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      p: 2.5,
                      borderRadius: '12px',
                      border: `2px solid ${selected ? '#2EBD36' : '#E5E7EB'}`,
                      bgcolor: '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxShadow: selected
                        ? '0 0 0 1px rgba(46, 189, 54, 0.15)'
                        : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {selected ? (
                        <CheckCircle sx={{ fontSize: 26, color: '#2EBD36' }} />
                      ) : (
                        <RadioButtonUnchecked
                          sx={{ fontSize: 26, color: '#D1D5DB' }}
                        />
                      )}
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '10px',
                          bgcolor: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 24, color: '#374151' }} />
                      </Box>
                      <Box sx={{ pr: 4 }}>
                        <Typography
                          sx={{
                            fontSize: '17px',
                            fontWeight: 700,
                            color: '#111827',
                            lineHeight: 1.4,
                          }}
                        >
                          {title}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>

            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
              }}
            >
              <InfoOutlined
                sx={{
                  fontSize: 22,
                  color: '#9CA3AF',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              />
              <Typography
                sx={{ fontSize: '14px', color: '#4B5563', lineHeight: 1.65 }}
              >
                <strong>로그인 아이디</strong>는 본인인증으로 확인된{' '}
                <strong>휴대전화 번호</strong>와 동일합니다. 사내 직원은 부서·
                혈액형 등 추가 정보를, 외부인은 <strong>사업장 코드·소속업체</strong>
                를 입력해야 합니다. 무엇을 입력해야 할지 모르시면 담당 관리자에게
                물어보시면 됩니다.
              </Typography>
            </Box>
          </Stack>
        ) : activeStep === 2 ? (
          <Stack sx={{ my: 3 }} spacing={3}>
            {/* 본인인증 (약관 이후 최상단) */}
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                border: '1px solid #E5E7EB',
                backgroundColor: '#F9FAFB',
              }}
            >
              <StyledLabel>본인인증</StyledLabel>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                {props.signUpInputs.identityConfirm === true ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ECFDF5',
                      color: '#2CA900',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      px: 1.5,
                      height: '48px',
                    }}
                  >
                    본인인증 완료
                  </Box>
                ) : (
                  <Button
                    sx={{
                      flex: 1,
                      backgroundColor: '#1F2937',
                      color: '#FFFFFF',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      height: '48px',
                      '&:hover': {
                        backgroundColor: '#374151',
                      },
                    }}
                    name={mokLoading ? '요청 중...' : '본인인증 시작'}
                    disabled={mokLoading}
                    onClick={HandleMokAuthClick}
                  />
                )}
              </Stack>

              <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 1 }}>
                본인인증 완료 후 이름·전화번호·생년월일이 자동 입력되며, 동일한
                전화번호가 <strong>로그인 아이디</strong>로 설정됩니다.
              </Typography>
              {props.signUpInputs.identityErrMsg && (
                <Typography
                  sx={{ fontSize: '13px', color: '#DC2626', mt: 0.5 }}
                >
                  {props.signUpInputs.identityErrMsg}
                </Typography>
              )}
            </Box>

            {/* 이름 */}
            <Box>
              <StyledLabel>
                {props.lanList.length > 0
                  ? props.lanList.filter((f) => f.lanId === 'Name')[0].lanNm
                  : '이름'}
              </StyledLabel>
              <StyledTextField
                fullWidth
                placeholder="본인인증 후 자동 입력"
                value={props.signUpInputs.userNm}
                onChange={(e) => {
                  props.setSignUpInputs({
                    ...props.signUpInputs,
                    userNm: e.target.value,
                  });
                }}
                onKeyUp={(e) => props.checkCapsLock(e)}
                disabled
              />
            </Box>

            {/* 전화번호 */}
            <Box>
              <StyledLabel>전화번호 (로그인 ID)</StyledLabel>
              <StyledTextField
                fullWidth
                placeholder="본인인증 후 자동 입력"
                value={props.signUpInputs.userTel}
                type="tel"
                disabled
              />
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#6B7280',
                  mt: 0.75,
                  lineHeight: 1.5,
                }}
              >
                로그인 시 사용하는 아이디는 위 본인인증 전화번호와{' '}
                <strong>동일한 번호</strong>입니다. 직접 입력·변경할 수 없으며,
                이미 가입된 번호는 중복 가입할 수 없습니다.
              </Typography>
            </Box>

            {/* 생년월일 (내부만 혈액형) */}
            {isExternalSignUp ? (
              <Box>
                <StyledLabel>생년월일</StyledLabel>
                <StyledTextField
                  fullWidth
                  placeholder="YYYY.MM.DD"
                  type="date"
                  value={props.signUpInputs.userBirth}
                  hasValue={!!props.signUpInputs.userBirth}
                  onChange={(e) => {
                    props.setSignUpInputs({
                      ...props.signUpInputs,
                      userBirth: e.target.value,
                    });
                  }}
                  disabled
                />
              </Box>
            ) : (
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <StyledLabel>생년월일</StyledLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="YYYY.MM.DD"
                    type="date"
                    value={props.signUpInputs.userBirth}
                    hasValue={!!props.signUpInputs.userBirth}
                    onChange={(e) => {
                      props.setSignUpInputs({
                        ...props.signUpInputs,
                        userBirth: e.target.value,
                      });
                    }}
                    disabled
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <StyledLabel>혈액형</StyledLabel>
                  <Combo
                    name=""
                    items={props.listOptions.bloodList}
                    value={props.signUpInputs.userBlood}
                    onChange={(e) => {
                      props.setSignUpInputs({
                        ...props.signUpInputs,
                        userBlood: e,
                      });
                    }}
                    fullWidth
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography
                            sx={{
                              color: '#9CA3AF',
                              fontSize: '14px',
                            }}
                          >
                            선택
                          </Typography>
                        );
                      }
                      const selectedItem = props.listOptions.bloodList.find(
                        (item) => item.value === value
                      );
                      return selectedItem ? selectedItem.name : '';
                    }}
                    wrapSx={comboWrapStyles}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                      },
                    }}
                  />
                </Box>
              </Stack>
            )}

            {/* 비밀번호 */}
            <Box>
              <StyledLabel>
                {props.lanList.length > 0
                  ? props.lanList.filter((f) => f.lanId === 'Password')[0].lanNm
                  : '비밀번호'}
              </StyledLabel>
              <StyledTextField
                fullWidth
                type="password"
                value={props.signUpInputs.userPw}
                onChange={(e) => {
                  props.setSignUpInputs({
                    ...props.signUpInputs,
                    userPw: e.target.value,
                  });
                }}
                onKeyUp={(e) => props.checkCapsLock(e)}
              />
            </Box>

            {/* 비밀번호 확인 */}
            <Box>
              <StyledLabel>
                {props.lanList.length > 0
                  ? props.lanList.filter(
                    (f) => f.lanId === 'PasswordConfirm'
                  )[0].lanNm
                  : '비밀번호 확인'}
              </StyledLabel>
              <StyledTextField
                fullWidth
                type="password"
                value={props.signUpInputs.userPwConfirm}
                onChange={(e) => {
                  props.setSignUpInputs({
                    ...props.signUpInputs,
                    userPwConfirm: e.target.value,
                  });
                }}
                onKeyUp={(e) => props.checkCapsLock(e)}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{ fontSize: '12px', color: '#6B7280', mt: '8px !important' }}
            >
              (8자리 이상, 대문자, 숫자, 특수문자(!@#$%^&*)가 포함되어야합니다.)
            </Typography>
            {props.capsLockFlag && (
              <Typography
                textAlign="right"
                color="red"
                sx={{ fontSize: '12px', mt: '8px !important' }}
              >
                Caps Lock이 켜져 있습니다
              </Typography>
            )}
            <Typography
              textAlign="right"
              color="red"
              sx={{ fontSize: '12px', mt: '8px !important' }}
            >
              {(props.signUpInputs.userPw !== '' ||
                props.signUpInputs.userPwConfirm !== '') &&
                props.signUpInputs.userPw !== props.signUpInputs.userPwConfirm
                ? '비밀번호가 다릅니다.'
                : null}
            </Typography>

            {/* 주소 (내부 회원만) */}
            {!isExternalSignUp && (
              <Box>
                <StyledLabel>주소</StyledLabel>
                <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                  <StyledTextField
                    fullWidth
                    placeholder="우편번호"
                    value={props.signUpInputs.userPost}
                    onChange={(e) =>
                      props.setSignUpInputs({
                        ...props.signUpInputs,
                        userPost: e.target.value,
                      })
                    }
                    disabled={
                      Commons.CheckMobileDevice() === 'ios' ? false : true
                    }
                  />
                  {Commons.CheckMobileDevice() === 'ios' ? null : (
                    <Button
                      sx={{
                        minWidth: '100px',
                        backgroundColor: '#1F2937',
                        color: '#FFFFFF',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#374151',
                        },
                      }}
                      name="주소검색"
                      onClick={() => setDialog1(true)}
                    />
                  )}
                </Stack>
                <StyledTextField
                  fullWidth
                  placeholder="기본 주소"
                  value={props.signUpInputs.userAddress1 ?? ''}
                  onChange={(e) =>
                    props.setSignUpInputs((prev) => ({
                      ...prev,
                      userAddress1: e.target.value,
                    }))
                  }
                  sx={{ mb: 1.5 }}
                />
                <StyledTextField
                  fullWidth
                  placeholder="상세 주소를 입력하세요"
                  value={props.signUpInputs.userAddress2 ?? ''}
                  onChange={(e) => {
                    props.setSignUpInputs((prev) => ({
                      ...prev,
                      userAddress2: e.target.value,
                    }));
                  }}
                />
              </Box>
            )}
            {/* 사업장코드 */}
            <Box>
              <StyledLabel>사업장코드</StyledLabel>
              <Stack direction="row" spacing={1.5}>
                <StyledTextField
                  fullWidth
                  placeholder="코드를 입력하세요"
                  value={props.signUpInputs.userCustCd ?? ''}
                  onChange={(e) => {
                    props.setSignUpInputs((prev) => ({
                      ...prev,
                      userCustCd: e.target.value,
                    }));
                  }}
                />
                {!isExternalSignUp && (
                  <Button
                    sx={{
                      minWidth: '100px',
                      backgroundColor: '#2EBD36',
                      color: '#FFFFFF',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none',
                      boxShadow: '0 10px 15px -3px rgba(46, 189, 54, 0.25)',
                      '&:hover': {
                        backgroundColor: '#16A34A',
                        boxShadow: '0 10px 15px -3px rgba(46, 189, 54, 0.35)',
                      },
                    }}
                    name="확인"
                    onClick={() =>
                      props.SearchDeptByCustCd(props.signUpInputs.userCustCd)
                    }
                  />
                )}
              </Stack>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <ClickAwayListener
                  onClickAway={() => setCustCodeTipOpen(false)}
                >
                  <Tooltip
                    title={
                      <React.Fragment>
                        사업장코드는 회원님이 소속된 사업장에 부여된 고유 식별
                        번호입니다.
                        <br />이 코드를 입력해야만 해당 사업장의 인력으로
                        시스템에 등록되어 서비스를 이용하실 수 있습니다.
                      </React.Fragment>
                    }
                    open={custCodeTipOpen}
                    onClose={() => setCustCodeTipOpen(false)}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    arrow
                    placement="top"
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [1, -30],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          options: {
                            padding: 16,
                            boundary: 'viewport',
                          },
                        },
                      ],
                    }}
                  >
                    <Typography
                      sx={{
                        px: 1,
                        pt: 2,
                        fontSize: '0.8rem',
                        color: '#707070ff',
                        cursor: 'pointer',
                        display: 'inline-block',
                        width: 'fit-content',
                      }}
                      onClick={() => setCustCodeTipOpen((prev) => !prev)}
                    >
                      •{' '}
                      <span style={{ textDecoration: 'underline' }}>
                        사업장코드란 무엇인가요?
                      </span>
                    </Typography>
                  </Tooltip>
                </ClickAwayListener>

                <ClickAwayListener
                  onClickAway={() => setCustCodeHowTipOpen(false)}
                >
                  <Tooltip
                    title={
                      <React.Fragment>
                        해당 코드는 사업장 관리자를 통해서만 발급받으실 수
                        있습니다.
                        <br />
                        회원 가입을 진행하시려면 소속 사업장의 관리자에게
                        문의하여 코드를 전달받아 입력해 주세요.
                      </React.Fragment>
                    }
                    open={custCodeHowTipOpen}
                    onClose={() => setCustCodeHowTipOpen(false)}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    arrow
                    placement="top"
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [8, -25],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          options: {
                            padding: 16,
                            boundary: 'viewport',
                          },
                        },
                      ],
                    }}
                  >
                    <Typography
                      sx={{
                        px: 1,
                        pt: 1,
                        fontSize: '0.8rem',
                        color: '#707070ff',
                        cursor: 'pointer',
                        display: 'inline-block',
                        alignSelf: 'flex-start',
                        width: 'fit-content',
                      }}
                      onClick={() => setCustCodeHowTipOpen((prev) => !prev)}
                    >
                      •{' '}
                      <span style={{ textDecoration: 'underline' }}>
                        사업장코드를 받는 방법
                      </span>
                    </Typography>
                  </Tooltip>
                </ClickAwayListener>
              </Box>
            </Box>

            {/* 부서 선택 (내부) / 소속·직위 직접 입력 (외부) */}
            {isExternalSignUp ? (
              <>
                <Box>
                  <StyledLabel>소속</StyledLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="소속을 입력하세요"
                    value={props.signUpInputs.affiliationNm || ''}
                    onChange={(e) => {
                      props.setSignUpInputs({
                        ...props.signUpInputs,
                        affiliationNm: e.target.value,
                      });
                    }}
                  />
                </Box>
                <Box>
                  <StyledLabel>직위</StyledLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="직위를 입력하세요"
                    value={props.signUpInputs.rank ?? ''}
                    onChange={(e) => {
                      props.setSignUpInputs((prev) => ({
                        ...prev,
                        rank: e.target.value,
                      }));
                    }}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box>
                  <StyledLabel>부서 선택</StyledLabel>
                  <Combo
                    name=""
                    items={props.deptOptions || []}
                    value={props.signUpInputs.userDepCd}
                    onChange={(value) => {
                      props.setSignUpInputs({
                        ...props.signUpInputs,
                        userDepCd: value,
                      });
                    }}
                    fullWidth
                    disabled={
                      !props.deptOptions || props.deptOptions.length === 0
                    }
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography
                            sx={{
                              color: '#9CA3AF',
                              fontSize: '14px',
                            }}
                          >
                            선택
                          </Typography>
                        );
                      }
                      const selectedItem = (props.deptOptions || []).find(
                        (item) => item.value === value
                      );
                      return selectedItem ? selectedItem.name : '';
                    }}
                    wrapSx={comboWrapStyles}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <StyledLabel>직위</StyledLabel>
                  <StyledTextField
                    fullWidth
                    placeholder="직위를 입력하세요"
                    value={props.signUpInputs.rank ?? ''}
                    onChange={(e) => {
                      props.setSignUpInputs((prev) => ({
                        ...prev,
                        rank: e.target.value,
                      }));
                    }}
                  />
                </Box>
              </>
            )}
          </Stack>
        ) : activeStep === 3 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              height: '100%',
              position: 'relative',
            }}
          >
            {/* 콘텐츠 영역 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: 'calc(100% - 72px)', // 확인 버튼 높이(48px) + mb(24px) 제외
              }}
            >
              {/* 환영 메시지 */}
              <Typography
                sx={{
                  mb: 3,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#111827',
                  lineHeight: 1.5,
                }}
              >
                세이프온의 안전에 오신 것을 <br />
                환영합니다.
              </Typography>

              {/* 체크마크 애니메이션 */}
              <Box
                sx={{
                  width: '120px',
                  height: '120px',
                  mx: 'auto',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Lottie
                  lottieRef={lottieRef}
                  animationData={SuccessAnimation}
                  loop={false}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>

              {/* 완료 메시지 */}
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: '#111827',
                  lineHeight: 1.6,
                  mb: 1,
                }}
              >
                세이프온 가입을 완료했어요.
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 400,
                  color: '#111827',
                  lineHeight: 1.6,
                }}
              >
                회원님의 아이디는{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    color: '#111827',
                  }}
                >
                  {props.signUpId}
                </Box>{' '}
                입니다.
              </Typography>
            </Box>

            {/* 확인 버튼 */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mb: 3,
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
              }}
            >
              <Button
                variant="contained"
                name="확인"
                onClick={() => props.CloseDialog()}
                sx={{
                  width: '100%',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#2EBD36',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 6px -1px rgba(46, 189, 54, 0.2)',
                  '&:hover': {
                    backgroundColor: '#16A34A',
                    boxShadow: '0 4px 6px -1px rgba(46, 189, 54, 0.3)',
                  },
                }}
              />
            </Box>
          </Box>
        ) : null}
        {activeStep < steps.length - 1 && (
          <Box
            sx={{
              position: 'fixed',
              bottom: `${navBar}px`,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'row',
              gap: 1.5,
              p: 2,
              backgroundColor: '#FFFFFF',
              borderTop: '1px solid #E5E7EB',
              zIndex: 10,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              name="이전"
              disabled={activeStep === 0}
              onClick={HandleBack}
              sx={{
                height: '55px',
                borderRadius: '16px',
                borderColor: '#E5E7EB',
                color: '#111827',
                fontSize: '17px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#D1D5DB',
                  backgroundColor: '#F9FAFB',
                },
                '&:disabled': {
                  borderColor: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            />
            <Button
              fullWidth
              variant="contained"
              name={activeStep === steps.length - 2 ? '완료' : '다음'}
              onClick={() => HandleNext(activeStep)}
              disabled={
                activeStep === 0
                  ? !(
                    props.agreement.serviceUsingCheck &&
                    props.agreement.serviceKindCheck &&
                    props.agreement.personalInformationCheck &&
                    props.agreement.transferAndCompensationCheck &&
                    props.agreement.disclaimerCheck
                  )
                  : activeStep === 1
                    ? !props.signUpInputs.memberSignUpType
                    : false
              }
              sx={{
                height: '55px',
                borderRadius: '16px',
                backgroundColor: '#2EBD36',
                color: '#FFFFFF',
                fontSize: '17px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 10px 15px -3px rgba(46, 189, 54, 0.25)',
                '&:hover': {
                  backgroundColor: '#16A34A',
                  boxShadow: '0 10px 15px -3px rgba(46, 189, 54, 0.35)',
                },
                '&:disabled': {
                  backgroundColor: '#D1D5DB',
                  color: '#FFFFFF',
                },
              }}
            />
          </Box>
        )}
      </React.Fragment>
      <KakaoPost
        open={dialog1}
        onClose={() => setDialog1(false)}
        title="주소 검색"
        ReceivePost={(_data) => RecievePosts(_data)}
      />

      <Dialog
        fullScreen
        open={dialog2.open}
        content={
          <Terms
            type={dialog2.type}
            title={dialog2.title}
            onClick={() => {
              switch (dialog2.type) {
                case '0':
                  props.setAgreement({
                    ...props.agreement,
                    serviceUsingCheck: true,
                  });
                  break;
                case '1':
                  props.setAgreement({
                    ...props.agreement,
                    serviceKindCheck: true,
                  });
                  break;
                case '2':
                  props.setAgreement({
                    ...props.agreement,
                    personalInformationCheck: true,
                  });
                  break;
                case '3':
                  props.setAgreement({
                    ...props.agreement,
                    transferAndCompensationCheck: true,
                  });
                  break;
                case '4':
                  props.setAgreement({
                    ...props.agreement,
                    disclaimerCheck: true,
                  });
                  break;
                default:
                  break;
              }
              setDialog2({ ...dialog2, open: false });
            }}
            onClose={() => setDialog2({ ...dialog2, open: false })}
          />
        }
        sx={{ '& .MuiDialog-paper': { margin: 0 } }}
        contentSx={{
          height: '100vh',
          px: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        actionsSx={{ p: 0 }}
        useCloseButton={false}
      />
    </Box>
  );
}

export default SignUp;
